import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Gem, Sparkles, Trophy } from "lucide-react";

export default function WeeklyChallengeBanner() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/challenges/weekly").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { challenge, completed, win } = data;
  const totalProgress =
    challenge.goals.reduce((acc, g) => acc + Math.min(1, g.current / g.target), 0) /
    challenge.goals.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      onClick={() => setCollapsed((c) => !c)}
      className="surface relative overflow-hidden cursor-pointer"
      style={{
        borderColor: completed ? "rgba(124,92,255,0.45)" : "rgba(184,164,255,0.18)",
        boxShadow: completed
          ? "0 0 0 1px rgba(124,92,255,0.30), 0 0 40px -10px rgba(124,92,255,0.55)"
          : "0 0 0 1px rgba(184,164,255,0.10), 0 0 32px -12px rgba(184,164,255,0.30)",
      }}
      data-testid="weekly-challenge"
    >
      <div
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${completed ? "rgba(124,92,255,0.40)" : "rgba(184,164,255,0.28)"}, transparent 65%)`,
          filter: "blur(20px)",
        }}
      />
      <div className="relative px-5 py-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: "linear-gradient(135deg, rgba(124,92,255,0.32), rgba(184,164,255,0.18))",
              border: "1px solid rgba(184,164,255,0.40)",
              boxShadow: "0 0 18px -4px rgba(184,164,255,0.65)",
            }}
          >
            {completed ? <Trophy size={18} className="text-[#B8A4FF]" /> : <Target size={18} className="text-[#B8A4FF]" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="eyebrow-muted">This week</span>
              {win && <span className="text-[10px] font-bold text-[#FFD86B] tracking-wider uppercase" style={{ textShadow: "0 0 10px rgba(255,216,107,0.6)" }}>Completed</span>}
            </div>
            <div className="text-[15px] font-semibold text-white mt-0.5">{challenge.name}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="hidden md:flex items-center gap-1.5 text-[12px] font-bold text-[#B8A4FF]">
              <Gem size={12} /> +{challenge.gem_reward}
            </div>
            <div className="hidden md:flex items-center gap-1.5 text-[12px] font-bold text-[#5AC8FA]">
              <Sparkles size={12} /> +{challenge.xp_reward} XP
            </div>
          </div>
        </div>

        {/* Combined progress bar */}
        <div className="mt-3 h-1.5 progress-track">
          <motion.div
            className="h-full progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${Math.round(totalProgress * 100)}%` }}
            transition={{ duration: 0.8 }}
          />
        </div>

        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <p className="text-[12.5px] text-[#B0AEBC] mt-3">{challenge.description}</p>
              <ul className="mt-2 space-y-1.5">
                {challenge.goals.map((g) => {
                  const pct = Math.min(100, Math.round((g.current / g.target) * 100));
                  const done = g.current >= g.target;
                  return (
                    <li key={g.key} className="flex items-center gap-2.5 text-[12px]">
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <span className={done ? "text-[#5AC8FA]" : "text-[#B0AEBC]"}>{g.label}</span>
                      </div>
                      <span className="tabular-nums font-semibold" style={{ color: done ? "#5AC8FA" : "#F4F4F5" }}>
                        {g.current.toLocaleString()} / {g.target.toLocaleString()}
                      </span>
                      <div className="w-20 h-1 progress-track">
                        <motion.div
                          className="h-full"
                          style={{
                            background: done
                              ? "linear-gradient(90deg, #5AC8FA, #7C5CFF)"
                              : "linear-gradient(90deg, #7C5CFF, #B8A4FF)",
                            borderRadius: 999,
                          }}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.7 }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
